import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Stethoscope, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  session: {
    id: string;
    topic?: string | null;
    module_code?: string | null;
    class?: string | null;
    closed_at?: string | null;
  };
}

/**
 * "Start Remedial Session" button shown on a closed session row.
 * Visible to head_academic / superadmin. Submits a remedial request which
 * Principal/Superadmin must approve before scanning re-opens (for absentees only).
 */
export function RemedialSessionDialog({ session }: Props) {
  const { user, hasRole } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<{ id: string; status: string; created_at: string } | null>(null);

  const canRequest = hasRole("head_academic") || hasRole("superadmin");

  useEffect(() => {
    if (!open || !canRequest) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("remedial_session_requests")
        .select("id, status, created_at")
        .eq("session_id", session.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setPending(data ?? null);
    })();
    return () => { cancelled = true; };
  }, [open, session.id, canRequest]);

  if (!canRequest) return null;

  function reset(next: boolean) { setOpen(next); if (!next) setReason(""); }

  async function submit() {
    if (reason.trim().length < 5) {
      toast({ title: "Reason required", description: "Describe why a remedial session is needed (5+ chars).", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("remedial_session_requests").insert({
      session_id: session.id,
      requested_by: user?.id,
      reason: reason.trim(),
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Could not submit", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Remedial request sent", description: "Awaiting Principal/Superadmin approval." });
    reset(false);
  }

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" title="Request a remedial session for absentees">
          <Stethoscope className="w-4 h-4 mr-1" />Start Remedial Session
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            Request remedial session
          </DialogTitle>
          <DialogDescription>
            For "{session.topic || "Untitled"}"{session.module_code ? ` (${session.module_code})` : ""}
            {session.class ? ` — ${session.class}` : ""}
            {session.closed_at ? `, closed ${format(new Date(session.closed_at), "PPp")}` : ""}.
            Once approved, only students who were Absent in this session can be scanned in the remedial session.
          </DialogDescription>
        </DialogHeader>

        {pending && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>A remedial request is already <Badge variant="outline">pending</Badge> (submitted {format(new Date(pending.created_at), "PPp")}).</span>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="rem-reason">Reason *</Label>
          <Textarea
            id="rem-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Power outage cut the original session short; absentees deserve a make-up scan."
            rows={3}
            disabled={!!pending}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => reset(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !!pending}>
            <Send className="w-4 h-4 mr-1" />Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
